import { OPEN_PROSPECT_STATUSES, PROSPECT_STATUS_LABELS, PROSPECT_STATUSES, type ProspectDTO, type ProspectStatus } from '@bifeai/shared';
import { Badge, Button, Dialog } from '@bifeai/ui';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { formatDate } from '../lib/format';
import { prospectWhat } from './sellers';
import './prospect-board.css';

const MANUAL: readonly ProspectStatus[] = ['NEW', 'CONTACTED', 'DEMO'];
const isOpen = (s: ProspectStatus) => (OPEN_PROSPECT_STATUSES as readonly string[]).includes(s);
const waLink = (phone: string) => `https://wa.me/${phone.replace(/\D/g, '')}`;

/**
 * Tablero de posibles clientes: Nuevo → Contactado → Demo agendada → Link de pago enviado → Ganado / Perdido.
 * Se arrastra la tarjeta a otra etapa. El link de pago lo arma BifeAI (solo el admin suelta en esa columna), "Ganado"
 * es solo cuando paga, y "Perdido" pide el motivo. Con el tachito de la tarjeta se borra (pide confirmación).
 */
export function ProspectBoard({
  items,
  admin,
  showSeller,
  onOpen,
  onStage,
  onLink,
  onLost,
  canDelete,
  onDelete,
}: {
  items: ProspectDTO[];
  /** El panel de BifeAI: arma links de pago y reabre perdidos. */
  admin: boolean;
  /** Mostrar a cargo de quién está (admin o jefe de equipo). */
  showSeller: boolean;
  onOpen: (p: ProspectDTO) => void;
  onStage: (p: ProspectDTO, status: 'NEW' | 'CONTACTED' | 'DEMO') => void;
  onLink?: (p: ProspectDTO) => void;
  onLost: (p: ProspectDTO) => void;
  /** Si muestra el tachito en esa tarjeta. Sin onDelete, no se borra desde el tablero. */
  canDelete?: (p: ProspectDTO) => boolean;
  onDelete?: (p: ProspectDTO) => Promise<unknown>;
}) {
  const [dragging, setDragging] = useState<ProspectDTO | null>(null);
  const [over, setOver] = useState<ProspectStatus | null>(null);
  const [deleting, setDeleting] = useState<ProspectDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const confirmDelete = async () => {
    if (!deleting || !onDelete) return;
    setBusy(true);
    try {
      await onDelete(deleting);
      setDeleting(null);
    } catch {
      // El aviso del error lo muestra la página; el diálogo queda abierto para reintentar.
    } finally {
      setBusy(false);
    }
  };

  const canDrop = (p: ProspectDTO | null, to: ProspectStatus): boolean => {
    if (!p || p.status === to || p.status === 'WON') return false;
    if (MANUAL.includes(to)) return isOpen(p.status) || (p.status === 'LOST' && admin);
    if (to === 'LINK_SENT') return admin && !!onLink && MANUAL.includes(p.status);
    if (to === 'LOST') return isOpen(p.status);
    return false;
  };
  const drop = (p: ProspectDTO, to: ProspectStatus) => {
    if (MANUAL.includes(to)) onStage(p, to as 'NEW' | 'CONTACTED' | 'DEMO');
    else if (to === 'LINK_SENT') onLink?.(p);
    else if (to === 'LOST') onLost(p);
  };

  return (
    <div className="bf-pboard">
      {PROSPECT_STATUSES.map((status) => {
        const list = items.filter((p) => p.status === status);
        const droppable = canDrop(dragging, status);
        const mine = dragging?.status === status;
        return (
          <section
            key={status}
            aria-label={PROSPECT_STATUS_LABELS[status]}
            className={`bf-pboard-col is-${status.toLowerCase()}${dragging && droppable ? ' can-drop' : ''}${dragging && !droppable && !mine ? ' no-drop' : ''}${over === status && droppable ? ' is-over' : ''}`}
            onDragOver={(e) => {
              if (!droppable) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (over !== status) setOver(status);
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              setOver((v) => (v === status ? null : v));
            }}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              if (dragging && droppable) drop(dragging, status);
              setDragging(null);
            }}
          >
            <h3>
              <span>
                <i className="bf-pboard-dot" aria-hidden />
                {PROSPECT_STATUS_LABELS[status]}
              </span>
              <span className="bf-pboard-count">{list.length}</span>
            </h3>
            {list.length === 0 && (
              <p className="bf-pboard-empty">
                {dragging && droppable ? 'Soltalo acá' : status === 'WON' ? 'Se pasa solo cuando paga' : 'Sin posibles clientes'}
              </p>
            )}
            {list.map((p) => (
              <article
                key={p.id}
                className={`bf-pcard${dragging?.id === p.id ? ' is-dragging' : ''}`}
                role="button"
                tabIndex={0}
                draggable={p.status !== 'WON'}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', p.id);
                  // En el cuadro siguiente: cambiar la página en el mismo instante a veces corta el arrastre.
                  requestAnimationFrame(() => setDragging(p));
                }}
                onDragEnd={() => {
                  setDragging(null);
                  setOver(null);
                }}
                onClick={() => onOpen(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(p);
                  }
                }}
              >
                <div className="bf-pcard-top">
                  <strong>{p.business}</strong>
                  <span className="bf-pcard-actions">
                    {onDelete && p.status !== 'WON' && (canDelete?.(p) ?? true) && (
                      <button
                        type="button"
                        className="bf-pcard-del"
                        title="Borrar"
                        aria-label={`Borrar ${p.business}`}
                        draggable={false}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleting(p);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Trash2 size={14} aria-hidden />
                      </button>
                    )}
                  </span>
                </div>
                <span className="bf-cell-sub">{p.contactName}</span>
                {p.phone && (
                  <a className="bf-pcard-wa" href={waLink(p.phone)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                    WhatsApp {p.phone}
                  </a>
                )}
                <span className="bf-cell-sub">{prospectWhat(p)}</span>
                {p.lostReason && <span className="bf-cell-sub">Motivo: {p.lostReason}</span>}
                <div className="bf-pcard-tags">
                  {p.source === 'WEB' && <Badge tone="info">Web</Badge>}
                  {showSeller && <Badge tone={p.sellerId ? 'neutral' : 'success'}>{p.sellerName ?? 'BifeAI (vos)'}</Badge>}
                  <span className="bf-cell-sub">{formatDate(p.createdAt)}</span>
                </div>
              </article>
            ))}
          </section>
        );
      })}
      <Dialog
        open={deleting !== null}
        onOpenChange={(v) => !busy && !v && setDeleting(null)}
        title={deleting ? `¿Borrar ${deleting.business}?` : ''}
        description="Sale del tablero y de la lista, y no se puede recuperar. Si no compró, también podés marcarlo como perdido para que quede el motivo."
      >
        <div className="bf-form-actions">
          <Button onClick={() => setDeleting(null)} disabled={busy}>
            No, dejarlo
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void confirmDelete()}>
            Borrar
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
